import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, PlanetName, KundaliChartData } from '../types/kundali';
import {
  Clock,
  X,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  Sparkles,
  CheckCircle2,
  Calendar,
  AlertTriangle,
  Info
} from 'lucide-react';
import { getPlanetShortCode } from '../utils/language';
import {
  getActive5LevelDashaPath,
  formatDashaMonthYear,
  formatDashaDayMonth,
  formatDashaDateYear,
  getMultiYearDashaHorizon,
  DashaHorizonItem
} from '../utils/dashaCalculator';
import { calculateVedicAspects } from './UnifiedDashaPanel';
import { Eye } from 'lucide-react';

interface FocusedTimingDrawerProps {
  kundali: CompleteKundali;
  chartData: KundaliChartData;
  lang?: Language;
  onClose: () => void;
  onSelectPlanet?: (planet: string) => void;
  onSelectHouse?: (house: number) => void;
}

export const FocusedTimingDrawer: React.FC<FocusedTimingDrawerProps> = ({
  kundali,
  chartData,
  lang = 'en',
  onClose,
  onSelectPlanet,
  onSelectHouse
}) => {
  const isHi = lang === 'hi';
  const { sadeSati, dashas, birthDetails, houses } = kundali;
  const [isFutureExpanded, setIsFutureExpanded] = useState(false);
  const [expandedFutureAdId, setExpandedFutureAdId] = useState<string | null>(null);

  // 1. Calculate Native's current biological age
  const birthDateStr = birthDetails?.date;
  const currentAge = useMemo(() => {
    if (!birthDateStr) return 30;
    const bMs = new Date(birthDateStr).getTime();
    if (isNaN(bMs)) return 30;
    return Number(((Date.now() - bMs) / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1));
  }, [birthDateStr]);

  const maxAllowedAge = Math.max(75, currentAge + 5);

  // 2. Focused Saturn Transits (Current Active + Next Phase within ~5 years)
  const saturnFocus = useMemo(() => {
    const timeline = sadeSati?.timeline || [];
    const nowMs = Date.now();
    const fiveYearsMs = nowMs + 5 * 365.25 * 24 * 60 * 60 * 1000;

    // Active phase
    const activePhase = timeline.find(p => p.status === 'Active') ||
      timeline.find(p => {
        const s = new Date(p.startDate).getTime();
        const e = new Date(p.endDate).getTime();
        return nowMs >= s && nowMs <= e;
      }) || null;

    // Next upcoming phase occurring within next ~5 years
    const upcomingPhases = timeline.filter(p => {
      const s = new Date(p.startDate).getTime();
      return s > nowMs && s <= fiveYearsMs;
    });

    const nextPhase = upcomingPhases.length > 0 ? upcomingPhases[0] : null;

    return {
      activePhase,
      nextPhase,
      isCompletelyFreeNext5Yrs: !activePhase && !nextPhase
    };
  }, [sadeSati]);

  // 3. Active 5-Level Surgical Path (MD -> AD -> PD -> SD -> Prana)
  const active5Levels = useMemo(() => {
    return getActive5LevelDashaPath(dashas);
  }, [dashas]);

  // Dynamic House Locator for active chart
  const getLordChartRole = (planetName: string) => {
    const pos = chartData.planets?.find(p => p.name === planetName);
    const placedHouse = pos?.house ?? null;
    const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
    const ascSignIdx = houses?.[0]?.signIndex ?? (chartData.ascendantIndex ?? 0);
    const ruledHouses: number[] = [];
    for (let h = 1; h <= 12; h++) {
      const sIdx = (ascSignIdx + h - 1) % 12;
      if (ZODIAC_LORDS[sIdx] === planetName) ruledHouses.push(h);
    }
    return { placedHouse, ruledHouses };
  };

  // 4. Future Timeline (Next 5 Years) Capped at age 75 or maxAllowedAge
  const futureHorizon = useMemo(() => {
    const rawList = getMultiYearDashaHorizon(dashas, birthDateStr, { maxPeriods: 10 });
    // Filter out periods starting after maxAllowedAge (Max age 75)
    return rawList.filter(item => {
      if (item.ageStart !== undefined && item.ageStart > maxAllowedAge) {
        return false;
      }
      return true;
    });
  }, [dashas, birthDateStr, maxAllowedAge]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-xs transition-opacity animate-in fade-in duration-200">
      <div className="w-full max-w-2xl bg-[#FBF9F4] h-full shadow-2xl flex flex-col border-l border-astro-border overflow-hidden animate-in slide-in-from-right duration-300">
        
        {/* Drawer Header */}
        <div className="p-4 border-b border-astro-border flex items-center justify-between bg-white/95 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-300 text-amber-800 flex items-center justify-center font-bold text-sm">
              <Clock className="w-4 h-4 text-amber-700" />
            </div>
            <div>
              <h3 className="font-bold text-astro-primary font-serif text-base flex items-center gap-2">
                <span>{isHi ? 'विस्तृत दशा एवं तात्कालिक समय' : 'Detailed Timing Focus'}</span>
                <span className="text-[10px] font-sans font-bold px-2 py-0.5 rounded-full bg-astro-teal-tint text-astro-secondary border border-astro-secondary/20">
                  {isHi ? 'अधिकतम ७५ वर्ष' : 'Max Age 75'}
                </span>
              </h3>
              <p className="text-[11px] text-astro-secondary-text">
                {isHi
                  ? 'सक्रिय क्षण (प्राण दशा तक) • शनि गोचर प्रभाव • आगामी ५ वर्ष का प्रवाह'
                  : 'Active Moment (down to Prana) • Saturn Focus • Next 5-Year Horizon'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-astro-bg text-astro-secondary-text hover:text-astro-primary transition-colors cursor-pointer border border-transparent hover:border-astro-border"
            title={isHi ? 'बंद करें' : 'Close drawer'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">

          {/* 1. CURATED SATURN TRANSIT FOCUS */}
          <div className="bg-white border border-[#E2DED5] rounded-xl p-3.5 sm:p-4 shadow-2xs space-y-2.5">
            <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
              <span className="text-xs font-bold text-astro-primary font-serif uppercase tracking-wider flex items-center gap-1.5">
                <span>🪐</span>
                <span>{isHi ? 'शनि गोचर प्रभाव (वर्तमान एवं आगामी ५ वर्ष)' : 'Saturn Transit Focus (Current & Next 5 Yrs)'}</span>
              </span>
              <span className="text-[10.5px] font-mono text-astro-secondary-text">
                {sadeSati?.saturnSign ? `Saturn in ${sadeSati.saturnSign}` : ''}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
              {/* Active Saturn Phase */}
              <div className={`p-3 rounded-lg border text-xs space-y-1 ${
                saturnFocus.activePhase
                  ? 'bg-amber-50/70 border-amber-300 text-amber-900'
                  : 'bg-emerald-50/60 border-emerald-200 text-emerald-900'
              }`}>
                <div className="flex items-center justify-between font-bold text-[11px]">
                  <span className="flex items-center gap-1">
                    {saturnFocus.activePhase ? (
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    )}
                    <span>{isHi ? 'वर्तमान स्थिति' : 'Active Status'}</span>
                  </span>
                  {saturnFocus.activePhase && (
                    <span className="text-[10px] font-mono bg-amber-100 text-amber-800 px-1.5 py-0.2 rounded border border-amber-300">
                      {isHi ? 'सक्रिय' : 'Active Now'}
                    </span>
                  )}
                </div>
                <div className="font-bold text-xs pt-0.5">
                  {saturnFocus.activePhase
                    ? `${saturnFocus.activePhase.nameEn} (${saturnFocus.activePhase.saturnSign})`
                    : (isHi ? 'कोई साढ़ेसाती / ढैया सक्रिय नहीं' : 'No Active Sade Sati / Dhaiyya')}
                </div>
                <div className="text-[10.5px] opacity-80 font-mono">
                  {saturnFocus.activePhase
                    ? `${saturnFocus.activePhase.formattedStartDate} – ${saturnFocus.activePhase.formattedEndDate}`
                    : (isHi ? 'शनि अनुकूल भाव में गोचररत है' : 'Saturn transiting favorable houses from Moon')}
                </div>
              </div>

              {/* Next Upcoming Phase within 5 Years */}
              <div className="p-3 rounded-lg border border-astro-border/80 bg-[#FAF8F5] text-xs space-y-1">
                <div className="flex items-center justify-between font-bold text-[11px] text-astro-secondary">
                  <span>{isHi ? 'आगामी प्रभाव (५ वर्ष में)' : 'Next Shift (Within 5 Yrs)'}</span>
                  <Calendar className="w-3.5 h-3.5 text-astro-secondary-text" />
                </div>
                {saturnFocus.nextPhase ? (
                  <>
                    <div className="font-bold text-xs text-astro-primary pt-0.5">
                      {saturnFocus.nextPhase.nameEn} ({saturnFocus.nextPhase.saturnSign})
                    </div>
                    <div className="text-[10.5px] text-astro-secondary-text font-mono">
                      {saturnFocus.nextPhase.formattedStartDate} – {saturnFocus.nextPhase.formattedEndDate}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-xs text-emerald-800 pt-0.5">
                      {isHi ? 'अगले ५ वर्ष तक पूर्णतः मुक्त' : 'Unburdened for Next 5 Years'}
                    </div>
                    <div className="text-[10.5px] text-astro-secondary-text">
                      {isHi ? 'कोई नया साढ़ेसाती / ढैया चक्र नहीं' : 'No upcoming Sade Sati or Dhaiyya phases through this window'}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 2. SURGICAL 5-LEVEL ACTIVE MOMENT (MD -> AD -> PD -> SD -> Prana) */}
          {active5Levels && (
            <div className="bg-white border border-[#E2DED5] rounded-xl p-3.5 sm:p-4 shadow-2xs space-y-3">
              <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
                <div>
                  <span className="text-xs font-bold text-astro-primary font-serif uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span>{isHi ? 'वर्तमान सक्रिय ५-स्तरीय सूक्ष्म चक्र' : 'Active 5-Level Surgical Dasha'}</span>
                  </span>
                  <p className="text-[10px] text-astro-secondary-text">
                    {isHi
                      ? 'महादशा (वर्ष) ➔ अंतर्दशा (मास) ➔ प्रत्यंतर (सप्ताह) ➔ सूक्ष्म (दिन) ➔ प्राण (घंटे)'
                      : 'MD (Years) ➔ AD (Months) ➔ PD (Weeks) ➔ Sookshma (Days) ➔ Prana (Hours)'}
                  </p>
                </div>
                <span className="text-[10.5px] font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 font-bold">
                  {isHi ? 'वर्तमान क्षण' : 'Real-Time'}
                </span>
              </div>

              {/* The 5 Level Path Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 pt-1">
                {/* Level 1: MD */}
                <div
                  onClick={() => onSelectPlanet?.(active5Levels.md.planet)}
                  className="p-2.5 rounded-lg border border-astro-border bg-[#FAF8F5] hover:border-amber-400 cursor-pointer transition-all space-y-1"
                >
                  <div className="text-[9.5px] font-bold text-astro-secondary uppercase tracking-widest">
                    L1 • MD
                  </div>
                  <div className="font-bold text-sm text-astro-primary font-serif">
                    {getPlanetShortCode(active5Levels.md.planet, lang)}
                  </div>
                  <div className="text-[9.5px] font-mono text-astro-secondary-text">
                    {formatDashaDateYear(active5Levels.md.startDate)}–{formatDashaDateYear(active5Levels.md.endDate)}
                  </div>
                  {getLordChartRole(active5Levels.md.planet).placedHouse && (
                    <div className="text-[9px] font-mono text-astro-secondary-text bg-white px-1 py-0.2 rounded border border-astro-border/60 truncate">
                      in H{getLordChartRole(active5Levels.md.planet).placedHouse}
                    </div>
                  )}
                  {(() => {
                    const placedH = getLordChartRole(active5Levels.md.planet).placedHouse;
                    const asps = calculateVedicAspects(active5Levels.md.planet, placedH);
                    if (asps.length === 0) return null;
                    return (
                      <div className="flex items-center gap-1 flex-wrap pt-0.5">
                        <span className="text-[8px] font-bold text-astro-secondary">Asp:</span>
                        {asps.map((a, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectHouse?.(a.houseNumber);
                            }}
                            className="px-1 py-0.2 rounded bg-white hover:bg-amber-100 text-astro-primary text-[8px] font-mono font-bold border border-astro-border/60"
                          >
                            H{a.houseNumber}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden mt-1">
                    <div className="bg-astro-primary h-full" style={{ width: `${active5Levels.md.progressPercent}%` }} />
                  </div>
                </div>

                {/* Level 2: AD */}
                <div
                  onClick={() => onSelectPlanet?.(active5Levels.ad.planet)}
                  className="p-2.5 rounded-lg border border-amber-300 bg-amber-50/50 hover:border-amber-500 cursor-pointer transition-all space-y-1"
                >
                  <div className="text-[9.5px] font-bold text-amber-800 uppercase tracking-widest">
                    L2 • AD
                  </div>
                  <div className="font-bold text-sm text-amber-900 font-serif">
                    {getPlanetShortCode(active5Levels.ad.planet, lang)}
                  </div>
                  <div className="text-[9.5px] font-mono text-amber-800">
                    {active5Levels.ad.daysRemaining}d {isHi ? 'शेष' : 'left'}
                  </div>
                  {getLordChartRole(active5Levels.ad.planet).placedHouse && (
                    <div className="text-[9px] font-mono text-amber-800 bg-white/80 px-1 py-0.2 rounded border border-amber-200 truncate">
                      in H{getLordChartRole(active5Levels.ad.planet).placedHouse}
                    </div>
                  )}
                  {(() => {
                    const placedH = getLordChartRole(active5Levels.ad.planet).placedHouse;
                    const asps = calculateVedicAspects(active5Levels.ad.planet, placedH);
                    if (asps.length === 0) return null;
                    return (
                      <div className="flex items-center gap-1 flex-wrap pt-0.5">
                        <span className="text-[8px] font-bold text-amber-900">Asp:</span>
                        {asps.map((a, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectHouse?.(a.houseNumber);
                            }}
                            className="px-1 py-0.2 rounded bg-amber-100/90 hover:bg-amber-200 text-amber-950 text-[8px] font-mono font-bold border border-amber-300"
                          >
                            H{a.houseNumber}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="w-full bg-amber-200 h-1 rounded-full overflow-hidden mt-1">
                    <div className="bg-amber-600 h-full" style={{ width: `${active5Levels.ad.progressPercent}%` }} />
                  </div>
                </div>

                {/* Level 3: PD */}
                <div
                  onClick={() => onSelectPlanet?.(active5Levels.pd.planet)}
                  className="p-2.5 rounded-lg border border-astro-border bg-white hover:border-amber-400 cursor-pointer transition-all space-y-1"
                >
                  <div className="text-[9.5px] font-bold text-astro-secondary uppercase tracking-widest">
                    L3 • PD
                  </div>
                  <div className="font-bold text-sm text-[#263633] font-serif">
                    {getPlanetShortCode(active5Levels.pd.planet, lang)}
                  </div>
                  <div className="text-[9.5px] font-mono text-astro-secondary-text">
                    {active5Levels.pd.daysRemaining}d {isHi ? 'शेष' : 'left'}
                  </div>
                  {getLordChartRole(active5Levels.pd.planet).placedHouse && (
                    <div className="text-[9px] font-mono text-astro-secondary-text bg-[#FAF8F5] px-1 py-0.2 rounded border border-astro-border/60 truncate">
                      in H{getLordChartRole(active5Levels.pd.planet).placedHouse}
                    </div>
                  )}
                  {(() => {
                    const placedH = getLordChartRole(active5Levels.pd.planet).placedHouse;
                    const asps = calculateVedicAspects(active5Levels.pd.planet, placedH);
                    if (asps.length === 0) return null;
                    return (
                      <div className="flex items-center gap-1 flex-wrap pt-0.5">
                        <span className="text-[8px] font-bold text-emerald-800">Asp:</span>
                        {asps.map((a, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectHouse?.(a.houseNumber);
                            }}
                            className="px-1 py-0.2 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-950 text-[8px] font-mono font-bold border border-emerald-200"
                          >
                            H{a.houseNumber}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden mt-1">
                    <div className="bg-astro-secondary h-full" style={{ width: `${active5Levels.pd.progressPercent}%` }} />
                  </div>
                </div>

                {/* Level 4: SD (Sookshma) */}
                <div
                  onClick={() => onSelectPlanet?.(active5Levels.sd.planet)}
                  className="p-2.5 rounded-lg border border-astro-border bg-white hover:border-amber-400 cursor-pointer transition-all space-y-1"
                >
                  <div className="text-[9.5px] font-bold text-astro-secondary uppercase tracking-widest">
                    L4 • SD
                  </div>
                  <div className="font-bold text-sm text-[#263633] font-serif">
                    {getPlanetShortCode(active5Levels.sd.planet, lang)}
                  </div>
                  <div className="text-[9.5px] font-mono text-astro-secondary-text">
                    {active5Levels.sd.hoursRemaining}h {isHi ? 'शेष' : 'left'}
                  </div>
                  <div className="text-[9px] font-mono text-astro-secondary-text bg-[#FAF8F5] px-1 py-0.2 rounded border border-astro-border/60 truncate">
                    {formatDashaDayMonth(active5Levels.sd.startDate)}–{formatDashaDayMonth(active5Levels.sd.endDate)}
                  </div>
                  <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden mt-1">
                    <div className="bg-teal-600 h-full" style={{ width: `${active5Levels.sd.progressPercent}%` }} />
                  </div>
                </div>

                {/* Level 5: PrD (Prana) */}
                <div
                  onClick={() => onSelectPlanet?.(active5Levels.prd.planet)}
                  className="p-2.5 rounded-lg border border-emerald-300 bg-emerald-50/40 hover:border-emerald-500 cursor-pointer transition-all space-y-1"
                >
                  <div className="text-[9.5px] font-bold text-emerald-800 uppercase tracking-widest flex items-center justify-between">
                    <span>L5 • PrD</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  </div>
                  <div className="font-bold text-sm text-emerald-900 font-serif">
                    {getPlanetShortCode(active5Levels.prd.planet, lang)}
                  </div>
                  <div className="text-[9.5px] font-mono text-emerald-800">
                    {active5Levels.prd.hoursRemaining}h {isHi ? 'शेष' : 'left'}
                  </div>
                  <div className="text-[9px] font-mono text-emerald-800 bg-white px-1 py-0.2 rounded border border-emerald-200 truncate">
                    {isHi ? 'तात्कालिक' : 'Hourly Trigger'}
                  </div>
                  <div className="w-full bg-emerald-200 h-1 rounded-full overflow-hidden mt-1">
                    <div className="bg-emerald-600 h-full" style={{ width: `${active5Levels.prd.progressPercent}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 3. FUTURE TIMELINE (NEXT 5 YEARS) - CLUBBED & COLLAPSED ACCORDION */}
          <div className="bg-white border border-[#E2DED5] rounded-xl overflow-hidden shadow-2xs">
            {/* Accordion Toggle Header */}
            <button
              type="button"
              onClick={() => setIsFutureExpanded(prev => !prev)}
              className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-[#FAF8F5] to-white hover:bg-astro-bg transition-colors cursor-pointer border-b border-astro-border/60 text-left"
            >
              <div className="flex items-center gap-2.5">
                <Calendar className="w-4 h-4 text-astro-secondary" />
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-astro-primary font-serif flex items-center gap-2">
                    <span>{isHi ? 'आगामी भविष्य कालखंड (अगले ५ वर्ष)' : 'Future Timeline (Next 5 Years)'}</span>
                    <span className="text-[10px] font-sans font-semibold text-astro-secondary bg-[#FAF6ED] px-2 py-0.2 rounded border border-astro-border">
                      {isHi ? `आयु: ${currentAge} से ${Math.min(75, Math.round(currentAge + 5))} वर्ष` : `Age ${currentAge} to ${Math.min(75, Math.round(currentAge + 5))}`}
                    </span>
                  </h4>
                  <p className="text-[10.5px] text-astro-secondary-text mt-0.5">
                    {isHi
                      ? 'महादशा एवं अंतर्दशा मील के पत्थर (अधिकतम ७५ वर्ष की आयु तक सीमित)'
                      : 'MD & AD Chapter Milestones • Click to expand/collapse'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs font-semibold text-astro-secondary">
                <span className="text-[11px] font-mono">
                  {isFutureExpanded ? (isHi ? 'संक्षिप्त करें' : 'Collapse') : (isHi ? 'विस्तार करें' : 'Expand')}
                </span>
                {isFutureExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>

            {/* Accordion Body: Renders when Expanded */}
            {isFutureExpanded && (
              <div className="p-4 space-y-3 bg-[#FCFBF9] animate-in fade-in duration-200">
                {futureHorizon.length === 0 ? (
                  <div className="p-4 text-center text-xs text-astro-secondary-text">
                    {isHi ? 'अधिकतम आयु सीमा (७५ वर्ष) के भीतर कोई आगामी अवधि नहीं है।' : 'No upcoming periods within the age 75 limit.'}
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {futureHorizon.map((period, idx) => {
                      const isExpanded = expandedFutureAdId === period.id;
                      return (
                        <div
                          key={period.id || idx}
                          className={`rounded-xl border transition-all ${
                            period.isCurrent
                              ? 'bg-amber-50/60 border-amber-300 shadow-2xs'
                              : 'bg-white border-astro-border/80'
                          }`}
                        >
                          {/* Era Shift Alert if crossing MDs */}
                          {period.isEraShift && (
                            <div className="px-3 py-1.5 bg-gradient-to-r from-amber-500/15 to-orange-500/10 border-b border-amber-200 text-amber-900 text-[10.5px] font-bold flex items-center justify-between">
                              <span>
                                ⚡ {isHi ? 'महादशा संधि (महापरिवर्तन):' : 'Macro Era Shift:'} {period.prevMdPlanet} ➔ {period.mdPlanet}
                              </span>
                              {period.ageStart !== undefined && (
                                <span className="font-mono text-[10px] bg-white/80 px-1.5 py-0.2 rounded border border-amber-200">
                                  {isHi ? `आयु ${period.ageStart} वर्ष` : `Age ${period.ageStart} yrs`}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="p-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* MD - AD Pair */}
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold text-astro-secondary font-mono">
                                  {getPlanetShortCode(period.mdPlanet, lang)}
                                </span>
                                <span className="text-gray-400 font-mono">—</span>
                                <span className="text-xs font-bold text-astro-primary font-serif">
                                  {getPlanetShortCode(period.adPlanet, lang)}
                                </span>
                              </div>

                              {/* Age Badge */}
                              {period.ageStart !== undefined && (
                                <span className="text-[10px] font-mono text-astro-secondary-text bg-[#FAF6ED] px-1.5 py-0.5 rounded border border-astro-border">
                                  {isHi ? `आयु ${period.ageStart}` : `Age ${period.ageStart}`}
                                </span>
                              )}

                              {/* Dasha Chidra Warning */}
                              {period.isDashaChidra && (
                                <span className="text-[9.5px] font-bold text-rose-800 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <AlertTriangle className="w-2.5 h-2.5 text-rose-600" />
                                  <span>{isHi ? 'दशा छिद्र' : 'Dasha Chidra'}</span>
                                </span>
                              )}

                              {/* MD-AD Angle */}
                              {period.relationshipWithMd && (
                                <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded border ${
                                  period.relationshipWithMd.nature === 'harmonious'
                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                    : period.relationshipWithMd.nature === 'frictional'
                                    ? 'bg-rose-50 text-rose-800 border-rose-200'
                                    : 'bg-gray-50 text-gray-700 border-gray-200'
                                }`}>
                                  {isHi ? period.relationshipWithMd.labelHi : period.relationshipWithMd.labelEn}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-3">
                              <span className="text-[10.5px] font-mono text-astro-secondary-text">
                                {formatDashaMonthYear(period.startDate)} – {formatDashaMonthYear(period.endDate)}
                              </span>

                              {/* Optional toggle to see 9 PDs of this future AD */}
                              <button
                                type="button"
                                onClick={() => setExpandedFutureAdId(isExpanded ? null : period.id)}
                                className="text-[10px] font-bold text-astro-secondary hover:text-astro-primary px-2 py-0.8 rounded border border-astro-border bg-white hover:bg-astro-bg transition-colors cursor-pointer flex items-center gap-1"
                              >
                                <span>{isExpanded ? (isHi ? 'PD छिपाएं' : 'Hide PDs') : (isHi ? '९ PD देखें' : 'View 9 PDs')}</span>
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </button>
                            </div>
                          </div>

                          {/* Expanded 9 PD micro-grid for this future AD */}
                          {isExpanded && (
                            <div className="border-t border-astro-border/70 p-2.5 bg-[#FAF8F5] grid grid-cols-3 sm:grid-cols-9 gap-1.5 animate-in fade-in duration-150">
                              {period.pratyantardashas.map((pd, pIdx) => (
                                <div
                                  key={pIdx}
                                  className="p-1.5 rounded bg-white border border-astro-border text-center space-y-0.5"
                                >
                                  <div className="text-[10px] font-bold text-astro-primary">
                                    {getPlanetShortCode(pd.planet, lang)}
                                  </div>
                                  <div className="text-[8.5px] font-mono text-astro-secondary-text truncate">
                                    {formatDashaDayMonth(pd.startDate)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Drawer Footer */}
        <div className="p-3.5 border-t border-astro-border bg-white flex items-center justify-between text-xs text-astro-secondary-text">
          <div className="flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-astro-secondary" />
            <span>
              {isHi
                ? 'सम्पूर्ण १२०-वर्षीय ऐतिहासिक चक्र मुख्य "विंशोत्तरी" टैब में उपलब्ध है।'
                : 'Full 120-year multi-generational archive is preserved in the main Vimshottari tab.'}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-lg bg-astro-primary text-white font-semibold hover:bg-astro-primary/90 transition-colors cursor-pointer text-xs"
          >
            {isHi ? 'पूर्ण' : 'Done'}
          </button>
        </div>

      </div>
    </div>
  );
};
